import React, { useEffect, useState } from "react";
import { Col, Row, Card, Form, Alert } from "@themesberg/react-bootstrap";
import SelectInput from "../../../components/SelectInput";
import { mainTreasuryModules } from "./modules";
import { getSector, postApi, fetchApi } from "../../../redux/api";
import { useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faUser } from "@fortawesome/free-solid-svg-icons";
import BackButton from "../../../components/BackButton";
import toast from "react-hot-toast";
import CustomTypeahead from "../../components/CustomTypeahead";
import AuditorScopeFields, { isAuditorRole } from "./AuditorScopeFields";
import { useNavigate, useParams } from "react-router-dom";
import { Routes } from "../../../routes";

export function getDistinctObjects(array, key1, key2) {
  return array?.filter(
    (obj, index, self) =>
      index ===
      self?.findIndex((o) => o[key1] === obj[key1] && o[key2] === obj[key2])
  );
}

export default () => {
  const currentUser = useSelector((state) => state.auth.user);
  const { id } = useParams();
  const isEdit = !!id;
  
  const [form, setForm] = useState({
    username: "",
    fullname: "",
    email: "",
    password: "",
    role: "",
    phone: "",
    status: "active",
    accessTo: [],
    functionalities: [],
    mda_code: "",
    mda_name: "",
    auditor_category: "",
    auditor_mdas: [],
    auditor_scope: "",
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [budgetSummary, setBudgetSummary] = useState([]);
  const history = useNavigate();

  // Fetch user data if editing
  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      fetchApi(`users/${id}`)
        .then((data) => {
          if (data.user) {
            const user = data.user;
            setForm({
              ...user,
              accessTo: user.accessTo ? user.accessTo.split(",") : [],
              functionalities: user.functionalities ? user.functionalities.split(",") : [],
              auditor_mdas: user.auditor_mdas
                ? String(user.auditor_mdas).split(",").filter(Boolean)
                : [],
              password: "", // Don't populate password for security
            });
          }
        })
        .catch((error) => {
          console.error("Error fetching user:", error);
          toast.error("Failed to load user data");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id, isEdit]);

  // Fetch MDA list
  useEffect(() => {
    getSector({ type: "mdas" }, "mdas", (d) => {
      setBudgetSummary(d || []);
    });
  }, []);

  const validateForm = () => {
    const newErrors = {};
    
    if (!form.username.trim()) {
      newErrors.username = "Username is required";
    }
    
    if (!form.fullname.trim()) {
      newErrors.fullname = "Full name is required";
    }
    
    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = "Email is invalid";
    }
    
    if (!isEdit && !form.password) {
      newErrors.password = "Password is required";
    }
    
    if (!form.role) {
      newErrors.role = "Role is required";
    }

    if (isAuditorRole(form.role)) {
      if (!form.auditor_category) {
        newErrors.auditor_category = "Choose an auditor category";
      } else if (
        form.auditor_category !== "Value for Money" &&
        !(form.auditor_mdas || []).length
      ) {
        newErrors.auditor_mdas = "Select at least one MDA for this auditor";
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const auditorMdaOptions = getDistinctObjects(
    budgetSummary
      ?.map((item) => ({ code: item.head, name: item.description }))
      .filter((item) => item.code && item.name) || [],
    "code",
    "name"
  ).sort((a, b) => String(a.name).localeCompare(String(b.name)));

  const handleChange = ({ target: { name, value } }) => {
    setForm((p) => {
      const next = { ...p, [name]: value };

      // Picking an auditor role grants the Auditor General module and its
      // screens, so a new auditor lands on a usable sidebar. The permission
      // checkboxes below still let an admin adjust it afterwards.
      if (name === "role" && /^(auditor general|auditor)$/i.test(String(value).trim())) {
        const auditorModule = mainTreasuryModules.find((m) => m.title === "Auditor General");
        if (auditorModule) {
          next.accessTo = Array.from(new Set([...(p.accessTo || []), auditorModule.title]));
          next.functionalities = Array.from(
            new Set([
              ...(p.functionalities || []),
              ...(auditorModule.subMenu || []).map((item) => item.title),
            ])
          );
        }
      }

      return next;
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(p => ({ ...p, [name]: "" }));
    }
  };

  const handleCheckboxChange = (item) => {
    const title = typeof item === 'string' ? item : item.title;
    if (form.accessTo.includes(title)) {
      let newList = form.accessTo.filter((a) => a !== title);
      setForm((prevForm) => ({ ...prevForm, accessTo: newList }));
    } else {
      let newList = [...form.accessTo, title];
      setForm((prevForm) => ({ ...prevForm, accessTo: newList }));
    }
  };

  const handleChildCheckBoxChange = (item) => {
    const title = typeof item === 'string' ? item : item.title;
    if (form.functionalities.includes(title)) {
      let newList = form.functionalities.filter((a) => a !== title);
      setForm((prevForm) => ({ ...prevForm, functionalities: newList }));
    } else {
      let newList = [...form.functionalities, title];
      setForm((prevForm) => ({ ...prevForm, functionalities: newList }));
    }
  };

  const submitUser = (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      if (
        isAuditorRole(form.role) &&
        (!form.auditor_category ||
          (form.auditor_category !== "Value for Money" && !(form.auditor_mdas || []).length))
      ) {
        toast.error("Complete the auditor scope fields");
      }
      return;
    }

    setLoading(true);
    const endpoint = isEdit ? `users/update/${id}` : `treasury-app/sign_up`;
    const submitData = {
      ...form,
      accessTo: form.accessTo.join(","),
      functionalities: form.functionalities.join(","),
      auditor_mdas: (form.auditor_mdas || []).join(","),
      auditor_scope:
        form.auditor_scope ||
        (form.auditor_category === "Value for Money" ? "capital" : "all"),
    };

    // Remove password if empty during edit
    if (isEdit && !submitData.password) {
      delete submitData.password;
    }

    postApi(endpoint, submitData)
      .then((resp) => {
        if (resp.success) {
          toast.success(isEdit ? "User updated successfully" : "User created successfully");
          // Navigate to user list page
          setTimeout(() => {
            history(Routes.userAdmin.index);
          }, 1000);
        } else {
          toast.error(resp.msg || "An error occurred");
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error("An error occurred");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  let aheadData = budgetSummary?.map((i) => ({
    head: `${i.head}-${i.description}`,
    description: i.description,
    code: i.head,
    mda_name: i.description,
    mda_code: i.head,
  }));

  const renderSubMenu = (subMenu) => {
    return subMenu.map((subItem, index) => (
      <div key={index} style={{ marginLeft: 20 }}>
        <Form.Check
          type="checkbox"
          label={subItem.title}
          checked={form.functionalities.includes(subItem.title)}
          onChange={() => handleChildCheckBoxChange(subItem)}
        />
      </div>
    ));
  };

  if (loading && isEdit) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <BackButton />
      <Card border="light" className="bg-white shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex align-items-center mb-4">
            <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
            <h5 className="mb-0">{isEdit ? "Edit User" : "Create New User"}</h5>
          </div>
          
          <Form onSubmit={submitUser}>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Username <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    value={form.username}
                    onChange={handleChange}
                    placeholder="Enter username"
                    isInvalid={!!errors.username}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.username}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="fullname"
                    value={form.fullname}
                    onChange={handleChange}
                    placeholder="Enter full name"
                    isInvalid={!!errors.fullname}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.fullname}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="Enter email address"
                    isInvalid={!!errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>
                    Password {!isEdit && <span className="text-danger">*</span>}
                    {isEdit && <small className="text-muted">(leave blank to keep current)</small>}
                  </Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder={isEdit ? "Enter new password" : "Enter password"}
                    isInvalid={!!errors.password}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.password}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Role <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="role"
                    value={form.role}
                    onChange={handleChange}
                    isInvalid={!!errors.role}
                  >
                    <option value="">Select Role</option>
                    <option value="Admin">Admin</option>
                    <option value="Auditor General">Auditor General</option>
                    <option value="Auditor">Auditor</option>
                    <option value="MDA ADMIN">MDA Admin</option>
                    <option value="USER">User</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.role}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6} className="mb-3">
                <CustomTypeahead
                  labelKey="mda_name"
                  label="Select MDA"
                  options={getDistinctObjects(aheadData, "mda_name", "mda_code")}
                  selected={form.mda_name ? [{ mda_name: form.mda_name, mda_code: form.mda_code }] : []}
                  onChange={(s) => {
                    if (s.length) {
                      setForm((p) => ({
                        ...p,
                        mda_code: s[0].mda_code,
                        mda_name: s[0].mda_name,
                      }));
                    } else {
                      setForm((p) => ({
                        ...p,
                        mda_code: "",
                        mda_name: "",
                      }));
                    }
                  }}
                />
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <AuditorScopeFields
              role={form.role}
              category={form.auditor_category}
              mdas={form.auditor_mdas || []}
              scope={form.auditor_scope}
              mdaOptions={auditorMdaOptions}
              onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
            />

            <h5 className="my-4">Permissions and Access Level</h5>
            <Alert variant="info" className="mb-3">
              <small>Select the modules and functionalities this user should have access to.</small>
            </Alert>
            
            <Row>
              {mainTreasuryModules.map((item, index) => (
                <Col md="4" key={index} className="mb-3">
                  <Card className="h-100">
                    <Card.Body className="p-3">
                      <Form.Check
                        type="checkbox"
                        label={<strong>{item.title}</strong>}
                        checked={form.accessTo.includes(item.title)}
                        onChange={() => handleCheckboxChange(item)}
                        className="mb-2"
                      />
                      {form.accessTo.includes(item.title) && item.subMenu && (
                        <div className="ms-3">
                          {renderSubMenu(item.subMenu)}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
              
              <Col md="4" className="mb-3">
                <Card className="h-100">
                  <Card.Body className="p-3">
                    <Form.Check
                      type="checkbox"
                      label={<strong>User Admin</strong>}
                      checked={form.accessTo.includes("User Admin")}
                      onChange={() => handleCheckboxChange("User Admin")}
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <div className="mt-4 text-center">
              <CustomButton
                variant="primary"
                type="submit"
                loading={loading}
                className="px-4"
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                {isEdit ? "Update User" : "Create User"}
              </CustomButton>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </>
  );
};
